import React from 'react'
import { alpha, Box, Button, Chip, CircularProgress, Typography } from '@mui/material'
import type { ThemeAIRunRecordRow, ThemeAIRunRecordTheme } from '../../types/gotypes'
import { emotionColour, sentimentColour } from '../../utils/themeAISentimentColours'

interface ResponseListProps {
  records: ThemeAIRunRecordRow[]
  total: number
  offset: number
  pageSize: number
  loading: boolean
  selectedCode?: string
  /** What the filter chip says: the theme's name, or e.g. "Sentiment: Positive". */
  selectedThemeName?: string
  onSelectTheme: (code: string | undefined) => void
  /** Set when a filter other than a theme narrows the list (Results' Sentiment and Emotion tabs). */
  filtered?: boolean
  onClearFilter?: () => void
  /** Each theme's colour, for its chip and the words it matched (#1523 item 6). */
  themeColour?: (code: string | undefined) => string | undefined
  onPageChange: (offset: number) => void
}

// Splits text around the first case-insensitive occurrence of `needle`, so
// the evidence for the selected theme can be marked in place.
const highlight = (text: string, needle: string | undefined, colour?: string): React.ReactNode => {
  if (!needle) return text
  const at = text.toLowerCase().indexOf(needle.toLowerCase())
  if (at < 0) return text
  return (
    <>
      {text.slice(0, at)}
      <Box
        component="mark"
        sx={(theme) => ({
          backgroundColor: alpha(colour ?? theme.palette.info.main, colour ? 0.28 : 0.18),
          color: 'inherit',
          borderRadius: 0.5,
          px: 0.25,
        })}
      >
        {text.slice(at, at + needle.length)}
      </Box>
      {text.slice(at + needle.length)}
    </>
  )
}

// The matched words, when they are only part of the response. A match on the
// whole response says nothing the response itself does not.
const partialExcerpt = (matchingText: string | undefined, sourceText: string): string | undefined => {
  const excerpt = matchingText?.trim()
  if (!excerpt || excerpt.toLowerCase() === sourceText.trim().toLowerCase()) return undefined
  return excerpt
}

// A theme's sentiment and emotion, as small chips. Each theme carries its own:
// one response can be Negative about one theme and Positive about another.
const ThemeTags: React.FC<{ theme: ThemeAIRunRecordTheme }> = ({ theme }) => {
  const tags = [
    { kind: 'Sentiment', value: theme.sentiment, colour: sentimentColour(theme.sentiment) },
    { kind: 'Emotion', value: theme.emotion, colour: emotionColour(theme.emotion) },
  ].filter((tag) => tag.value)
  if (tags.length === 0) return null
  return (
    <>
      {tags.map((tag) => (
        <Chip
          key={tag.kind}
          size="small"
          label={tag.value}
          aria-label={`${theme.name} ${tag.kind.toLowerCase()}: ${tag.value}`}
          variant={tag.colour ? 'filled' : 'outlined'}
          sx={{
            flexShrink: 0,
            textTransform: 'capitalize',
            ...(tag.colour ? { backgroundColor: tag.colour, color: 'rgba(0, 0, 0, 0.82)' } : {}),
          }}
        />
      ))}
    </>
  )
}

// The responses behind the numbers: each coded response, every theme it was
// coded with and the words each theme was matched on. Choosing a theme (here
// or on the distribution) filters the list to that theme and marks its
// matched words in the response.
const ResponseList: React.FC<ResponseListProps> = ({
  records,
  total,
  offset,
  pageSize,
  loading,
  selectedCode,
  selectedThemeName,
  onSelectTheme,
  filtered = selectedCode !== undefined,
  onClearFilter,
  themeColour,
  onPageChange,
}) => {
  const selectedColour = themeColour?.(selectedCode)
  const first = total === 0 ? 0 : offset + 1
  const last = Math.min(offset + records.length, total)

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, minHeight: 32, flexWrap: 'wrap' }}>
        <Typography variant="subtitle2">Responses</Typography>
        {filtered && (
          <Chip
            size="small"
            color="info"
            label={selectedThemeName || selectedCode}
            onDelete={onClearFilter ?? (() => onSelectTheme(undefined))}
            sx={selectedColour ? { backgroundColor: selectedColour } : undefined}
          />
        )}
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontVariantNumeric: 'tabular-nums' }}>
          {loading && records.length === 0 ? 'Loading…' : `${first.toLocaleString()}–${last.toLocaleString()} of ${total.toLocaleString()}`}
        </Typography>
        {loading && <CircularProgress size={14} />}
      </Box>

      {!loading && records.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
          {filtered ? 'No responses match this filter.' : 'No coded responses yet.'}
        </Typography>
      ) : (
        <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0, opacity: loading ? 0.6 : 1, transition: 'opacity 150ms ease-out' }}>
          {records.map((record) => {
            const selectedMatch = record.themes.find((t) => t.code === selectedCode)
            // The selected theme is named once, in the header chip; each row
            // lists only the other themes the response also carries.
            const otherThemes = record.themes.filter((t) => t.code !== selectedCode)
            return (
              <Box
                component="li"
                key={record.record_id}
                sx={{ py: 1.5, borderTop: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 0.75 }}
              >
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', columnGap: 2, alignItems: 'baseline' }}>
                  <Typography variant="body2" sx={{ maxWidth: '75ch', lineHeight: 1.6 }}>
                    {highlight(record.source_text, partialExcerpt(selectedMatch?.matching_text, record.source_text), selectedColour)}
                  </Typography>
                  <Typography variant="caption" color="text.disabled">{record.interview_id}</Typography>
                </Box>
                {selectedMatch?.explanation && (
                  <Typography variant="caption" color="text.secondary" sx={{ maxWidth: '75ch' }}>
                    Why: {selectedMatch.explanation}
                  </Typography>
                )}
                {otherThemes.length > 0 && (
                  <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {otherThemes.map((theme) => {
                      const excerpt = partialExcerpt(theme.matching_text, record.source_text)
                      return (
                        <Box component="li" key={theme.code} sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0 }}>
                          {selectedCode !== undefined && theme === otherThemes[0] && (
                            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>Also</Typography>
                          )}
                          <Chip
                            size="small"
                            variant="outlined"
                            label={theme.name}
                            onClick={() => onSelectTheme(theme.code)}
                            sx={{ flexShrink: 0, ...(themeColour?.(theme.code) ? { borderColor: themeColour(theme.code) } : {}) }}
                          />
                          <ThemeTags theme={theme} />
                          {(excerpt || theme.explanation) && (
                            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 0 }}>
                              {[excerpt && `“${excerpt}”`, theme.explanation].filter(Boolean).join(' — ')}
                            </Typography>
                          )}
                        </Box>
                      )
                    })}
                  </Box>
                )}
                {record.error ? (
                  <Typography variant="caption" color="error" data-testid="response-not-coded">
                    Not coded: {record.error}
                  </Typography>
                ) : (
                  record.themes.length === 0 && <Typography variant="caption" color="text.secondary">No theme</Typography>
                )}
                {selectedMatch && (selectedMatch.sentiment || selectedMatch.emotion) && (
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                    <ThemeTags theme={selectedMatch} />
                  </Box>
                )}
              </Box>
            )
          })}
        </Box>
      )}

      {total > pageSize && (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1 }}>
          <Button size="small" color="info" disabled={offset === 0 || loading} onClick={() => onPageChange(Math.max(0, offset - pageSize))}>
            Previous
          </Button>
          <Button size="small" color="info" disabled={last >= total || loading} onClick={() => onPageChange(offset + pageSize)}>
            Next
          </Button>
        </Box>
      )}
    </Box>
  )
}

export default ResponseList
